import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight,
  Filter,
  GitBranch,
  GitCompare,
  Grid3x3,
  Info,
  Network,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Switch } from "@/components/ui/switch";
import { API, formatNumber, getPlatformName, PLATFORM_COLORS, PLATFORM_CHART_COLORS } from "./constants";
import { useAuth } from "@/contexts/AuthContext";
import { ContentStrategyReport } from "./spillover/ContentStrategyReport";
import { SpilloverNetworkVisualization } from "./spillover/SpilloverNetworkVisualization";
import { SpilloverExportCenter } from "./spillover/SpilloverExportCenter";
import { SpilloverComparison } from "./spillover/SpilloverComparison";
import { SpilloverPatternInsights } from "./spillover/SpilloverPatternInsights";

const T = {
  ko: {
    title: "스필오버 효과 분석",
    subtitle: "어떤 플랫폼의 바이럴 게시물이 다른 플랫폼 팔로워 변화와 연결되는지 방향성 중심으로 확인합니다.",
    selectGroup: "아티스트 그룹 선택",
    noGroups: "아티스트 그룹이 없습니다",
    noGroupsDesc: "아티스트 그룹 관리에서 먼저 그룹을 생성하세요",
    flowTab: "플랫폼 흐름",
    networkTab: "Network Visualization",
    exportTab: "Export",
    eventsTab: "이벤트 로그",
    timelineTab: "타임라인",
    aiTab: "콘텐츠 전략 리포트",
    flowNetworkTab: "플로우 & 네트워크",
    eventsTimelineTab: "이벤트 & 타임라인",
    strategyExportTab: "전략 & Export",
    matrixView: "매트릭스",
    graphView: "네트워크 그래프",
    sankeyView: "Sankey",
    metricLabel: "기준 지표",
    adjustedMetric: "보정 Lift 우선",
    observedMetric: "관측 변화 우선",
    confidenceLabel: "신뢰도",
    allFilter: "전체",
    rankingsTitle: "플랫폼 랭킹",
    compareMode: "기간 비교",
    compareModeDesc: "현재 기간 vs 이전 동일 기간",
    currentPeriod: "현재",
    previousPeriod: "이전",
    comparisonFailed: "비교 데이터를 불러오지 못했습니다",
    noEvents: "분석 기간 내 스필오버 이벤트가 없습니다",
    noEventsDesc: "게시물/팔로워 스냅샷 데이터가 더 쌓이면 방향성 분석이 시작됩니다",
    noData: "데이터가 부족합니다",
    days7: "7일",
    days14: "14일",
    days30: "30일",
    days60: "60일",
    days90: "90일",
    viralPosts: "바이럴 게시물 수",
    positiveRatio: "긍정 효과 비율",
    strongestSource: "가장 강한 소스 플랫폼",
    strongestTarget: "가장 많이 반응한 타깃",
    topPair: "가장 강한 플랫폼 페어",
    pairMatrix: "플랫폼 영향 매트릭스",
    pairMatrixDesc: "행은 소스 플랫폼, 열은 영향을 받은 타깃 플랫폼입니다.",
    sourcePlatform: "소스 플랫폼",
    targetPlatform: "타깃 플랫폼",
    eventCount: "이벤트 수",
    avgDelta: "평균 변화",
    totalDelta: "누적 변화",
    positiveEvents: "긍정 이벤트",
    methodology: "수치 해석 가이드",
    methodologyDesc: "현재 수치는 인과를 확정하는 값이 아니라, 게시물 전후에 관측된 방향성 있는 변화입니다.",
    sourceRanking: "소스 플랫폼 랭킹",
    targetRanking: "타깃 플랫폼 랭킹",
    playbookTab: "실행 플레이북",
    playbookTitle: "그룹별 Spillover Playbook",
    playbookDesc: "보정 Lift를 기준으로 어떤 플랫폼에서 시작해 어디로 재배포할지 실행 우선순위를 정합니다.",
    priorityFlows: "우선 실행 플로우",
    watchouts: "주의할 플로우",
    operatingRules: "운영 규칙",
    observationWindow: "관측 윈도우",
    estimateLabel: "추정 영향",
    adjustedLift: "보정 Lift",
    observedDelta: "관측 변화",
    timingWindow: "권장 재배포 시점",
    recommendedPackaging: "권장 포맷",
    baselineUnavailable: "baseline 데이터가 부족합니다",
    competingEvents: "중첩 바이럴",
    playbookMetricNote: "보정 Lift는 같은 길이의 직전 성장분을 기대치로 보고, 실제 관측 변화에서 차감한 값입니다.",
    confidence: "신뢰도",
    impactDirection: "방향성",
    mixed: "혼합",
    positive: "긍정",
    negative: "부정",
    high: "높음",
    medium: "보통",
    low: "낮음",
    allSources: "모든 소스",
    allTargets: "모든 타깃",
    filters: "필터",
    followerDelta: "팔로워 변화",
    growthRate: "성장률",
    sourceToTarget: "소스 → 타깃",
    postedAt: "게시일",
    metricViews: "조회수",
    metricLikes: "좋아요",
    metricComments: "댓글",
    generateAI: "리포트 생성",
    generating: "생성 중...",
    aiNote: "콘텐츠 전략 리포트는 스필오버 데이터를 바탕으로 자동 생성됩니다.",
    analysisFailed: "스필오버 분석을 불러오지 못했습니다",
    timelineFailed: "타임라인을 불러오지 못했습니다",
    aiFailed: "콘텐츠 전략 리포트 생성에 실패했습니다",
    cachedResult: "캐시된 결과",
    posts: "스필오버로 감지된 바이럴 게시물",
    strongestEffect: "가장 큰 반응",
    windowHelp: "게시일 이전 가장 가까운 스냅샷과 게시 후 7일 내 마지막 스냅샷을 비교합니다.",
    viralScoreHelp: "해당 계정 최근 게시물 평균 likes/views 대비 배수입니다. 플랫폼 간 절대 비교 지표는 아닙니다.",
    positiveRatioHelp: "타깃 플랫폼별 효과 중 팔로워 순증이 나온 비율입니다.",
    topPairHelp: "보정 Lift 기준으로 가장 강한 소스→타깃 조합입니다.",
    timelineHint: "점선은 실제 스필오버 이벤트로 감지된 바이럴 게시물 게시일입니다.",
  },
  en: {
    title: "Spillover Effect Analysis",
    subtitle: "See which platform's viral posts align with follower movement on other platforms.",
    selectGroup: "Select Artist Group",
    noGroups: "No artist groups",
    noGroupsDesc: "Create groups in Artist Group Management first",
    flowTab: "Platform Flow",
    networkTab: "Network Visualization",
    exportTab: "Export",
    eventsTab: "Event Log",
    timelineTab: "Timeline",
    aiTab: "Content Strategy Report",
    flowNetworkTab: "Flow & Network",
    eventsTimelineTab: "Events & Timeline",
    strategyExportTab: "Strategy & Export",
    matrixView: "Matrix",
    graphView: "Network Graph",
    sankeyView: "Sankey",
    metricLabel: "Metric",
    adjustedMetric: "Adjusted Lift first",
    observedMetric: "Observed Delta first",
    confidenceLabel: "Confidence",
    allFilter: "All",
    rankingsTitle: "Platform Rankings",
    compareMode: "Compare Periods",
    compareModeDesc: "Current period vs previous equivalent period",
    currentPeriod: "Current",
    previousPeriod: "Previous",
    comparisonFailed: "Failed to load comparison data",
    noEvents: "No spillover events in the analysis period",
    noEventsDesc: "More post and follower snapshot data is needed before directional analysis becomes visible.",
    noData: "Insufficient data",
    days7: "7 days",
    days14: "14 days",
    days30: "30 days",
    days60: "60 days",
    days90: "90 days",
    viralPosts: "Viral Posts",
    positiveRatio: "Positive Effect Ratio",
    strongestSource: "Strongest Source",
    strongestTarget: "Strongest Target",
    topPair: "Top Platform Pair",
    pairMatrix: "Platform Influence Matrix",
    pairMatrixDesc: "Rows are source platforms and columns are affected target platforms.",
    sourcePlatform: "Source",
    targetPlatform: "Target",
    eventCount: "Events",
    avgDelta: "Average Delta",
    totalDelta: "Total Delta",
    positiveEvents: "Positive Events",
    methodology: "How to Read These Numbers",
    methodologyDesc: "These values show directional movement around a viral post, not strict causation.",
    sourceRanking: "Source Ranking",
    targetRanking: "Target Ranking",
    playbookTab: "Action Playbook",
    playbookTitle: "Group Spillover Playbook",
    playbookDesc: "Use adjusted lift to decide where to seed and where to redistribute next.",
    priorityFlows: "Priority Flows",
    watchouts: "Watchouts",
    operatingRules: "Operating Rules",
    observationWindow: "Observation Window",
    estimateLabel: "Estimated Impact",
    adjustedLift: "Adjusted Lift",
    observedDelta: "Observed Delta",
    timingWindow: "Recommended Redistribution Window",
    recommendedPackaging: "Recommended Packaging",
    baselineUnavailable: "Baseline history is insufficient",
    competingEvents: "Overlapping Viral Events",
    playbookMetricNote: "Adjusted lift subtracts the equivalent pre-period growth from the observed post-event movement.",
    confidence: "Confidence",
    impactDirection: "Direction",
    mixed: "Mixed",
    positive: "Positive",
    negative: "Negative",
    high: "High",
    medium: "Medium",
    low: "Low",
    allSources: "All Sources",
    allTargets: "All Targets",
    filters: "Filters",
    followerDelta: "Follower Delta",
    growthRate: "Growth Rate",
    sourceToTarget: "Source → Target",
    postedAt: "Posted",
    metricViews: "Views",
    metricLikes: "Likes",
    metricComments: "Comments",
    generateAI: "Generate Report",
    generating: "Generating...",
    aiNote: "The content strategy report is generated automatically from spillover data.",
    analysisFailed: "Failed to load spillover analysis",
    timelineFailed: "Failed to load timeline",
    aiFailed: "Failed to generate content strategy report",
    cachedResult: "Cached result",
    posts: "Viral posts used as spillover events",
    strongestEffect: "Strongest Effect",
    windowHelp: "Compares the closest snapshot before the post date with the last snapshot within 7 days after it.",
    viralScoreHelp: "A multiple versus the account's own recent average likes/views. It is not a cross-platform absolute score.",
    positiveRatioHelp: "Share of target-platform effects where follower counts moved up.",
    topPairHelp: "The source→target pair with the strongest adjusted lift.",
    timelineHint: "Dashed lines mark viral post dates that were actually used in the spillover event calculation.",
  },
  zh: {
    title: "溢出效应分析",
    subtitle: "查看哪个平台的热门帖子与其他平台的粉丝变化相关联。",
    selectGroup: "选择艺人分组",
    noGroups: "暂无艺人分组",
    noGroupsDesc: "请先在艺人分组管理中创建分组",
    flowTab: "平台流向",
    networkTab: "网络可视化",
    exportTab: "导出",
    eventsTab: "事件日志",
    timelineTab: "时间线",
    aiTab: "内容策略报告",
    flowNetworkTab: "流向 & 网络",
    eventsTimelineTab: "事件 & 时间线",
    strategyExportTab: "策略 & 导出",
    matrixView: "矩阵",
    graphView: "网络图",
    sankeyView: "桑基图",
    metricLabel: "指标",
    adjustedMetric: "校正 Lift 优先",
    observedMetric: "观测变化优先",
    confidenceLabel: "置信度",
    allFilter: "全部",
    rankingsTitle: "平台排名",
    compareMode: "期间对比",
    compareModeDesc: "当前期间 vs 上一同等期间",
    currentPeriod: "当前",
    previousPeriod: "上一期",
    comparisonFailed: "加载对比数据失败",
    noEvents: "分析期间内无溢出事件",
    noEventsDesc: "需要更多帖子和粉丝快照数据后才能开始方向性分析。",
    noData: "数据不足",
    days7: "7天",
    days14: "14天",
    days30: "30天",
    days60: "60天",
    days90: "90天",
    viralPosts: "热门帖子数",
    positiveRatio: "正面效果比例",
    strongestSource: "最强来源平台",
    strongestTarget: "响应最大的目标",
    topPair: "最强平台配对",
    pairMatrix: "平台影响矩阵",
    pairMatrixDesc: "行是来源平台，列是受影响的目标平台。",
    sourcePlatform: "来源平台",
    targetPlatform: "目标平台",
    eventCount: "事件数",
    avgDelta: "平均变化",
    totalDelta: "累计变化",
    positiveEvents: "正面事件",
    methodology: "数值解读指南",
    methodologyDesc: "这些数值展示的是热门帖子前后的方向性变化，非严格因果关系。",
    sourceRanking: "来源平台排名",
    targetRanking: "目标平台排名",
    playbookTab: "执行手册",
    playbookTitle: "分组溢出手册",
    playbookDesc: "基于校正 Lift 决定从哪个平台开始，以及下一步重新分发的方向。",
    priorityFlows: "优先执行流向",
    watchouts: "需注意的流向",
    operatingRules: "运营规则",
    observationWindow: "观测窗口",
    estimateLabel: "估计影响",
    adjustedLift: "校正 Lift",
    observedDelta: "观测变化",
    timingWindow: "建议重新分发时机",
    recommendedPackaging: "建议格式",
    baselineUnavailable: "基线数据不足",
    competingEvents: "重叠热门事件",
    playbookMetricNote: "校正 Lift 是从观测到的事件后变化中减去同等时长的前期增长得出的。",
    confidence: "置信度",
    impactDirection: "方向性",
    mixed: "混合",
    positive: "正面",
    negative: "负面",
    high: "高",
    medium: "中",
    low: "低",
    allSources: "所有来源",
    allTargets: "所有目标",
    filters: "筛选",
    followerDelta: "粉丝变化",
    growthRate: "增长率",
    sourceToTarget: "来源 → 目标",
    postedAt: "发布日期",
    metricViews: "浏览量",
    metricLikes: "点赞",
    metricComments: "评论",
    generateAI: "生成报告",
    generating: "生成中...",
    aiNote: "内容策略报告根据溢出数据自动生成。",
    analysisFailed: "加载溢出分析失败",
    timelineFailed: "加载时间线失败",
    aiFailed: "生成内容策略报告失败",
    cachedResult: "缓存结果",
    posts: "作为溢出事件检测到的热门帖子",
    strongestEffect: "最强效果",
    windowHelp: "比较发帖日期前最近的快照与发帖后7天内的最后一个快照。",
    viralScoreHelp: "相对于该账户近期平均点赞/浏览量的倍数，非跨平台绝对指标。",
    positiveRatioHelp: "目标平台效果中粉丝数上升的比例。",
    topPairHelp: "校正 Lift 最强的来源→目标组合。",
    timelineHint: "虚线标记的是实际用于溢出事件计算的热门帖子发布日期。",
  },
};

const CONFIDENCE_STYLES = {
  high: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
  medium: "border-amber-500/30 bg-amber-500/10 text-amber-500",
  low: "border-slate-500/30 bg-slate-500/10 text-slate-500",
};

const DIRECTION_STYLES = {
  positive: "border-emerald-500/30 bg-emerald-500/10 text-emerald-500",
  negative: "border-rose-500/30 bg-rose-500/10 text-rose-500",
  mixed: "border-violet-500/30 bg-violet-500/10 text-violet-500",
  flat: "border-slate-500/30 bg-slate-500/10 text-slate-500",
};

const getSignedCompactNumber = (value) => {
  const absolute = formatNumber(Math.abs(value || 0));
  if (!value) return absolute;
  return `${value > 0 ? "+" : "-"}${absolute}`;
};

const getSignedPreciseNumber = (value) => `${value > 0 ? "+" : ""}${(value || 0).toLocaleString()}`;

const getDeltaTone = (value) => {
  if (value > 0) return "text-emerald-500";
  if (value < 0) return "text-rose-500";
  return "text-muted-foreground";
};

const getConfidenceLabel = (confidence, t) => t[confidence] || confidence;

const getDirectionLabel = (direction, t) => t[direction] || direction;

const getLocalizedPlatformName = (platform, lang) => getPlatformName(platform, lang);

const getPackagingLabel = (packaging, lang) => {
  const labels = {
    "short-form clip": lang === "ko" ? "숏폼 클립" : lang === "zh" ? "短视频片段" : "Short-form clip",
    "caption-led recap or quote card": lang === "ko" ? "캡션형 요약 / 카드형 재가공" : lang === "zh" ? "标题式回顾 / 引用卡片" : "Caption-led recap / quote card",
    "long-form recap or behind-the-scenes cut": lang === "ko" ? "롱폼 요약 / 비하인드 컷" : lang === "zh" ? "长视频回顾 / 幕后花絮" : "Long-form recap / behind-the-scenes cut",
    "localized repost": lang === "ko" ? "현지화 리포스트" : lang === "zh" ? "本地化转发" : "Localized repost",
  };
  return labels[packaging] || packaging;
};

const getPlaybookRuleText = (rule, lang) => {
  if (!rule) return "";

  if (rule.rule_type === "seed_source") {
    return lang === "ko"
      ? `${getLocalizedPlatformName(rule.platform, lang)}를 1차 확산 플랫폼으로 두고 바이럴 신호가 나오면 다음 채널로 확장하세요.`
      : lang === "zh"
      ? `将 ${getLocalizedPlatformName(rule.platform, lang)} 作为首要扩散平台，当出现爆款信号时扩展到其他渠道。`
      : `Use ${getLocalizedPlatformName(rule.platform, lang)} as the first amplification channel, then fan out when a post breaks out.`;
  }

  if (rule.rule_type === "reinforce_target") {
    return lang === "ko"
      ? `${getLocalizedPlatformName(rule.platform, lang)}는 반응 흡수력이 높으므로 2차 배포 우선순위를 높게 유지하세요.`
      : lang === "zh"
      ? `${getLocalizedPlatformName(rule.platform, lang)} 对溢出效果的吸收力较强，建议保持较高的二次分发优先级。`
      : `${getLocalizedPlatformName(rule.platform, lang)} absorbs spillover well, so keep it high in the second-wave distribution queue.`;
  }

  if (rule.rule_type === "top_pair_window") {
    return lang === "ko"
      ? `${getLocalizedPlatformName(rule.source_platform, lang)} → ${getLocalizedPlatformName(rule.target_platform, lang)} 조합은 평균 ${rule.timing_window_days}일 안에 재배포하는 것이 좋습니다.`
      : lang === "zh"
      ? `${getLocalizedPlatformName(rule.source_platform, lang)} → ${getLocalizedPlatformName(rule.target_platform, lang)} 组合建议在 ${rule.timing_window_days} 天内进行二次分发。`
      : `For ${getLocalizedPlatformName(rule.source_platform, lang)} → ${getLocalizedPlatformName(rule.target_platform, lang)}, redistribute within about ${rule.timing_window_days} days.`;
  }

  return "";
};

const PlatformBadge = ({ platform, lang = "ko", testId }) => (
  <Badge
    data-testid={testId}
    variant="outline"
    className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em]"
    style={{ borderColor: PLATFORM_COLORS[platform], color: PLATFORM_COLORS[platform] }}
  >
    {getLocalizedPlatformName(platform, lang)}
  </Badge>
);

const MetricHint = ({ content, testId }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground/60 transition-colors hover:text-foreground"
          data-testid={testId}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs leading-relaxed">{content}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const SummaryMetricCard = ({ title, value, subvalue, icon: Icon, toneClass, hint, testId }) => (
  <Card data-testid={testId} className="border-border/60">
    <CardContent className="flex items-start justify-between gap-3 p-4">
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70">
          <span>{title}</span>
          {hint ? <MetricHint content={hint} testId={`${testId}-hint`} /> : null}
        </div>
        <div className={`text-2xl font-semibold ${toneClass || "text-foreground"}`}>{value}</div>
        {subvalue ? <p className="text-xs text-muted-foreground">{subvalue}</p> : null}
      </div>
      <div className="rounded-full border border-border/60 bg-muted/50 p-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
    </CardContent>
  </Card>
);

const TopPairCard = ({ pair, lang, t }) => {
  if (!pair) return null;
  const primaryLift = pair.total_adjusted_lift ?? pair.total_follower_delta;
  return (
    <Card data-testid="top-platform-pair-card" className="border-primary/20 bg-gradient-to-r from-primary/8 via-background to-background">
      <CardContent className="flex flex-col gap-3 p-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/70">
            <span>{t.topPair}</span>
            <MetricHint content={t.topPairHelp} testId="top-pair-hint" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
            <PlatformBadge platform={pair.source_platform} lang={lang} testId="top-pair-source-badge" />
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <PlatformBadge platform={pair.target_platform} lang={lang} testId="top-pair-target-badge" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.adjustedLift}</p>
            <p className={`text-lg font-semibold ${getDeltaTone(primaryLift)}`}>{primaryLift == null ? "-" : getSignedCompactNumber(primaryLift)}</p>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.observedDelta}</p>
            <p className={`text-lg font-semibold ${getDeltaTone(pair.total_follower_delta)}`}>{getSignedCompactNumber(pair.total_follower_delta)}</p>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.eventCount}</p>
            <p className="text-lg font-semibold text-foreground">{pair.event_count}</p>
          </div>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.positiveRatio}</p>
            <p className="text-lg font-semibold text-foreground">{pair.positive_ratio}%</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const RankingCard = ({ title, items, lang, t, testId, type = "source" }) => (
  <Card data-testid={testId} className="border-border/60">
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-semibold">{title}</CardTitle>
    </CardHeader>
    <CardContent className="space-y-3">
      {(items || []).slice(0, 5).map((item, index) => (
        <div key={`${type}-${item.platform}`} className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-background/70 p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">
              {index + 1}
            </div>
            <div>
              <PlatformBadge platform={item.platform} lang={lang} testId={`${testId}-${item.platform}-badge`} />
              <p className="mt-1 text-xs text-muted-foreground">
                {type === "source" ? `${t.eventCount} ${item.event_count}` : `${t.eventCount} ${item.effect_count}`}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-sm font-semibold ${getDeltaTone(item.total_adjusted_lift ?? item.total_follower_delta)}`}>
              {item.total_adjusted_lift == null ? getSignedCompactNumber(item.total_follower_delta) : getSignedCompactNumber(item.total_adjusted_lift)}
            </p>
            <p className="text-xs text-muted-foreground">{item.total_adjusted_lift == null ? t.observedDelta : t.adjustedLift}</p>
            <p className="text-xs text-muted-foreground">{getSignedCompactNumber(item.total_follower_delta)} {t.observedDelta}</p>
          </div>
        </div>
      ))}
    </CardContent>
  </Card>
);

const PairMatrix = ({ analysis, lang, t }) => {
  const platforms = analysis?.summary?.platforms_analyzed || [];
  const pairMap = useMemo(() => {
    const map = {};
    for (const pair of analysis?.pair_summary || []) {
      map[`${pair.source_platform}:${pair.target_platform}`] = pair;
    }
    return map;
  }, [analysis?.pair_summary]);
  const gridTemplateColumns = useMemo(
    () => `minmax(140px, 160px) repeat(${platforms.length}, minmax(140px, 1fr))`,
    [platforms.length],
  );

  return (
    <Card data-testid="spillover-pair-matrix-card" className="border-border/60">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Network className="h-4 w-4 text-primary" />
          {t.pairMatrix}
        </CardTitle>
        <CardDescription>{t.pairMatrixDesc}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto" data-testid="spillover-pair-matrix-table-wrapper">
          <div className="min-w-[720px]">
            <div className="grid gap-2" style={{ gridTemplateColumns }}>
              <div className="rounded-xl border border-dashed border-border/60 bg-muted/40 p-3 text-xs font-medium text-muted-foreground">
                {t.sourcePlatform} \ {t.targetPlatform}
              </div>
              {platforms.map((platform) => (
                <div key={`header-${platform}`} className="rounded-xl border border-border/60 bg-muted/30 p-3 text-center" data-testid={`matrix-target-${platform}`}>
                  <PlatformBadge platform={platform} lang={lang} testId={`matrix-target-badge-${platform}`} />
                </div>
              ))}
              {platforms.map((sourcePlatform) => (
                <Fragment key={`row-${sourcePlatform}`}>
                  <div className="rounded-xl border border-border/60 bg-muted/30 p-3" data-testid={`matrix-source-${sourcePlatform}`}>
                    <PlatformBadge platform={sourcePlatform} lang={lang} testId={`matrix-source-badge-${sourcePlatform}`} />
                  </div>
                  {platforms.map((targetPlatform) => {
                    const pair = pairMap[`${sourcePlatform}:${targetPlatform}`];
                    const isSamePlatform = sourcePlatform === targetPlatform;
                    const toneClass = pair ? (pair.total_follower_delta > 0 ? "border-emerald-500/30 bg-emerald-500/8" : pair.total_follower_delta < 0 ? "border-rose-500/30 bg-rose-500/8" : "border-border/60 bg-background") : "border-border/60 bg-background";

                    return (
                      <div
                        key={`${sourcePlatform}-${targetPlatform}`}
                        className={`rounded-xl border p-3 ${isSamePlatform ? "border-dashed border-border/50 bg-muted/20" : toneClass}`}
                        data-testid={`matrix-cell-${sourcePlatform}-${targetPlatform}`}
                      >
                        {isSamePlatform ? (
                          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">—</div>
                        ) : pair ? (
                          <div className="space-y-1.5">
                            <p className={`text-sm font-semibold ${getDeltaTone(pair.total_adjusted_lift ?? pair.total_follower_delta)}`}>
                              {pair.total_adjusted_lift == null ? getSignedCompactNumber(pair.total_follower_delta) : getSignedCompactNumber(pair.total_adjusted_lift)}
                            </p>
                            <p className="text-[11px] text-muted-foreground">{pair.total_adjusted_lift == null ? t.observedDelta : t.adjustedLift}</p>
                            <p className="text-[11px] text-muted-foreground">{getSignedCompactNumber(pair.total_follower_delta)} {t.observedDelta}</p>
                            <p className="text-[11px] text-muted-foreground">{t.eventCount} {pair.event_count}</p>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <p className="text-sm font-semibold text-muted-foreground">0</p>
                            <p className="text-[11px] text-muted-foreground">{t.adjustedLift}</p>
                            <p className="text-[11px] text-muted-foreground">0 {t.observedDelta}</p>
                            <p className="text-[11px] text-muted-foreground">{t.eventCount} 0</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EventCard = ({ event, index, expanded, onToggle, targetFilter, lang, t }) => {
  const visibleEffects = targetFilter === "all"
    ? event.effects
    : event.effects.filter((effect) => effect.platform === targetFilter);

  if (!visibleEffects.length) return null;

  return (
    <Card
      data-testid={`spillover-event-${index}`}
      className="cursor-pointer border-border/60 transition-all hover:border-primary/30"
      onClick={() => onToggle(index)}
    >
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <PlatformBadge platform={event.source_account.platform} lang={lang} testId={`event-source-badge-${index}`} />
              <Badge variant="outline" className={DIRECTION_STYLES[event.dominant_direction] || DIRECTION_STYLES.mixed} data-testid={`event-direction-${index}`}>
                {t.impactDirection} · {getDirectionLabel(event.dominant_direction, t)}
              </Badge>
              <Badge variant="outline" className={CONFIDENCE_STYLES[event.event_confidence] || CONFIDENCE_STYLES.medium} data-testid={`event-confidence-${index}`}>
                {t.confidence} · {getConfidenceLabel(event.event_confidence, t)}
              </Badge>
              <Badge variant="outline" className="border-yellow-500/30 bg-yellow-500/10 text-yellow-600" data-testid={`event-viral-score-${index}`}>
                Viral {event.viral_post.viral_score}x
              </Badge>
            </div>
            <div>
              <p className="truncate text-sm font-semibold text-foreground md:text-base">{event.viral_post.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {event.source_account.display_name} · {t.postedAt} {event.viral_post.published_at?.slice(0, 10)} · {t.observationWindow} {event.observation_window_days}{lang === "ko" ? "일" : lang === "zh" ? "天" : "d"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid={`event-flow-row-${index}`}>
              {visibleEffects.map((effect) => (
                <div key={`${event.viral_post.id}-${effect.platform}`} className="flex items-center gap-2 rounded-full border border-border/60 bg-muted/30 px-3 py-1.5 text-xs">
                  <PlatformBadge platform={event.source_account.platform} lang={lang} testId={`event-chip-source-${index}-${effect.platform}`} />
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <PlatformBadge platform={effect.platform} lang={lang} testId={`event-chip-target-${index}-${effect.platform}`} />
                  <span className={`font-semibold ${getDeltaTone(effect.follower_delta)}`}>{getSignedCompactNumber(effect.follower_delta)}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span>{t.metricViews} {formatNumber(event.viral_post.views)}</span>
              <span>{t.metricLikes} {formatNumber(event.viral_post.likes)}</span>
              <span>{t.metricComments} {formatNumber(event.viral_post.comments)}</span>
            </div>
          </div>
          <div className="rounded-2xl border border-border/60 bg-background/80 p-4 text-right lg:min-w-[180px]" data-testid={`event-total-impact-${index}`}>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/60">{t.adjustedLift}</p>
            <p className={`mt-1 text-2xl font-semibold ${getDeltaTone(event.adjusted_total_lift ?? event.total_spillover)}`}>
              {event.adjusted_total_lift == null ? "-" : getSignedCompactNumber(event.adjusted_total_lift)}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">{getSignedCompactNumber(event.total_spillover)} {t.observedDelta}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t.positiveEvents} {event.positive_target_count} / {event.effects.length}</p>
            <p className="text-xs text-muted-foreground">{t.competingEvents} {event.competing_viral_events}</p>
            <p className="text-xs text-muted-foreground">{t.strongestEffect}: {getLocalizedPlatformName(event.strongest_effect.platform, lang)}</p>
          </div>
        </div>

        {expanded ? (
          <div className="grid gap-3 border-t border-border/50 pt-4 md:grid-cols-2 xl:grid-cols-3">
            {visibleEffects.map((effect) => (
              <div key={`${event.viral_post.id}-${effect.account_id}`} className="rounded-2xl border border-border/60 bg-muted/20 p-3" data-testid={`event-effect-${index}-${effect.platform}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <PlatformBadge platform={effect.platform} lang={lang} testId={`event-effect-badge-${index}-${effect.platform}`} />
                    <span className="text-sm font-medium text-foreground">{effect.display_name}</span>
                  </div>
                  <Badge variant="outline" className={CONFIDENCE_STYLES[effect.confidence] || CONFIDENCE_STYLES.medium}>
                    {getConfidenceLabel(effect.confidence, t)}
                  </Badge>
                </div>
                <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                  <p>{effect.snap_before_date} → {effect.snap_after_date}</p>
                  <p>{formatNumber(effect.follower_before)} → {formatNumber(effect.follower_after)}</p>
                  <p>{t.observationWindow}: -{effect.days_before_post}{lang === "ko" ? "일" : lang === "zh" ? "天" : "d"} / +{effect.days_after_post}{lang === "ko" ? "일" : lang === "zh" ? "天" : "d"}</p>
                  <p>{t.competingEvents}: {effect.competing_viral_events}</p>
                </div>
                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.followerDelta}</p>
                    <p className={`text-lg font-semibold ${getDeltaTone(effect.follower_delta)}`}>{getSignedPreciseNumber(effect.follower_delta)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.growthRate}</p>
                    <p className={`text-sm font-semibold ${getDeltaTone(effect.growth_rate)}`}>{effect.growth_rate > 0 ? "+" : ""}{effect.growth_rate}%</p>
                  </div>
                </div>
                <div className="mt-3 rounded-xl border border-border/50 bg-background/70 p-3 text-xs">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.adjustedLift}</p>
                  {effect.baseline_available ? (
                    <>
                      <p className={`mt-1 text-sm font-semibold ${getDeltaTone(effect.adjusted_follower_lift)}`}>{getSignedPreciseNumber(effect.adjusted_follower_lift)}</p>
                      <p className="mt-1 text-muted-foreground">expected {getSignedPreciseNumber(effect.expected_follower_delta)} · raw {getSignedPreciseNumber(effect.follower_delta)}</p>
                    </>
                  ) : (
                    <p className="mt-1 text-muted-foreground">{t.baselineUnavailable}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const PlaybookTab = ({ analysis, lang, t }) => {
  const playbook = analysis?.playbook;
  const strongestSourceLift = playbook?.strongest_adjusted_source?.positive_adjusted_lift ?? playbook?.strongest_adjusted_source?.total_adjusted_lift;
  const strongestTargetLift = playbook?.strongest_adjusted_target?.positive_adjusted_lift ?? playbook?.strongest_adjusted_target?.total_adjusted_lift;

  if (!playbook) {
    return (
      <Card data-testid="spillover-playbook-empty-state">
        <CardContent className="py-12 text-center">
          <Network className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">{t.noEvents}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card data-testid="spillover-playbook-hero" className="border-primary/20 bg-gradient-to-r from-primary/6 via-background to-background">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">{t.playbookTitle}</CardTitle>
          <CardDescription>{t.playbookDesc}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-border/60 bg-background/80 p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/60">{t.strongestSource}</p>
            <p className={`mt-2 text-xl font-semibold ${getDeltaTone(strongestSourceLift)}`}>
              {playbook.strongest_adjusted_source ? getLocalizedPlatformName(playbook.strongest_adjusted_source.platform, lang) : "-"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {strongestSourceLift == null ? "-" : getSignedCompactNumber(strongestSourceLift)} {t.adjustedLift}
            </p>
          </div>
          <div className="rounded-2xl border border-border/60 bg-background/80 p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/60">{t.strongestTarget}</p>
            <p className={`mt-2 text-xl font-semibold ${getDeltaTone(strongestTargetLift)}`}>
              {playbook.strongest_adjusted_target ? getLocalizedPlatformName(playbook.strongest_adjusted_target.platform, lang) : "-"}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {strongestTargetLift == null ? "-" : getSignedCompactNumber(strongestTargetLift)} {t.adjustedLift}
            </p>
          </div>
          <div className="rounded-2xl border border-border/60 bg-background/80 p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/60">{t.topPair}</p>
            {playbook.top_adjusted_pair ? (
              <>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <PlatformBadge platform={playbook.top_adjusted_pair.source_platform} lang={lang} testId="playbook-top-source-badge" />
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  <PlatformBadge platform={playbook.top_adjusted_pair.target_platform} lang={lang} testId="playbook-top-target-badge" />
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{getSignedCompactNumber(playbook.top_adjusted_pair.total_adjusted_lift)} {t.adjustedLift}</p>
              </>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">-</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="spillover-playbook-note-card" className="border-border/60">
        <CardContent className="p-4 text-sm text-muted-foreground">{t.playbookMetricNote}</CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <Card data-testid="spillover-playbook-priority-card" className="border-border/60">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{t.priorityFlows}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(playbook.priority_flows || []).length ? playbook.priority_flows.map((flow, index) => (
              <div key={`${flow.source_platform}-${flow.target_platform}-${index}`} className="rounded-2xl border border-border/60 bg-background/70 p-4" data-testid={`playbook-priority-flow-${index}`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <PlatformBadge platform={flow.source_platform} lang={lang} testId={`priority-flow-source-${index}`} />
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      <PlatformBadge platform={flow.target_platform} lang={lang} testId={`priority-flow-target-${index}`} />
                      <Badge variant="outline" className={CONFIDENCE_STYLES[flow.confidence] || CONFIDENCE_STYLES.medium}>{getConfidenceLabel(flow.confidence, t)}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {lang === "ko"
                        ? `${getLocalizedPlatformName(flow.source_platform, lang)}에서 강한 반응이 나오면 ${flow.timing_window_days}일 안에 ${getLocalizedPlatformName(flow.target_platform, lang)}로 확산하세요.`
                        : lang === "zh"
                        ? `当 ${getLocalizedPlatformName(flow.source_platform, lang)} 出现爆款时，请在 ${flow.timing_window_days} 天内向 ${getLocalizedPlatformName(flow.target_platform, lang)} 进行二次分发。`
                        : `If ${getLocalizedPlatformName(flow.source_platform, lang)} breaks out, redistribute to ${getLocalizedPlatformName(flow.target_platform, lang)} within ${flow.timing_window_days} days.`}
                    </p>
                    <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
                      <p>{t.recommendedPackaging}: {getPackagingLabel(flow.recommended_packaging, lang)}</p>
                      <p>{t.timingWindow}: {flow.timing_window_days}{lang === "ko" ? "일" : lang === "zh" ? "天" : " days"}</p>
                      <p>{t.adjustedLift}: {getSignedCompactNumber(flow.total_adjusted_lift)}</p>
                      <p>{t.observedDelta}: {getSignedCompactNumber(flow.total_follower_delta)}</p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/50 bg-muted/20 p-3 text-right">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground/60">{t.positiveRatio}</p>
                    <p className="text-lg font-semibold text-foreground">{flow.positive_ratio}%</p>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-sm text-muted-foreground">{t.baselineUnavailable}</p>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card data-testid="spillover-playbook-watchouts-card" className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">{t.watchouts}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(playbook.watchouts || []).length ? playbook.watchouts.map((flow, index) => (
                <div key={`${flow.source_platform}-${flow.target_platform}-${index}`} className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-3" data-testid={`playbook-watchout-${index}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <PlatformBadge platform={flow.source_platform} lang={lang} testId={`watchout-source-${index}`} />
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <PlatformBadge platform={flow.target_platform} lang={lang} testId={`watchout-target-${index}`} />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {lang === "ko"
                      ? `이 조합은 보정 Lift가 음수여서 재배포 우선순위를 낮추는 편이 좋습니다.`
                      : lang === "zh"
                      ? `此组合的校正 Lift 为负值，建议降低其在二次分发中的优先级。`
                      : `This pair shows negative adjusted lift, so keep it lower in the redistribution queue.`}
                  </p>
                  <p className="mt-2 text-sm font-semibold text-rose-500">{getSignedCompactNumber(flow.total_adjusted_lift)}</p>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground">-</p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="spillover-playbook-rules-card" className="border-border/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">{t.operatingRules}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(playbook.operating_rules || []).map((rule, index) => (
                <div key={`${rule.rule_type}-${index}`} className="rounded-2xl border border-border/50 bg-background/70 p-3 text-sm text-muted-foreground" data-testid={`playbook-rule-${index}`}>
                  {getPlaybookRuleText(rule, lang)}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default function SpilloverPage({ lang = "ko" }) {
  const t = T[lang] || T.ko;
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState("flow-network");
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [targetFilter, setTargetFilter] = useState("all");
  const networkSnapshotRef = useRef(null);
  const [vizMode, setVizMode] = useState("matrix");
  const [metricMode, setMetricMode] = useState("adjusted");
  const [confidenceFilter, setConfidenceFilter] = useState("all");
  const [compareMode, setCompareMode] = useState(false);
  const [previousAnalysis, setPreviousAnalysis] = useState(null);
  const [previousTimeline, setPreviousTimeline] = useState(null);
  const [loadingComparison, setLoadingComparison] = useState(false);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await axios.get(`${API}/spillover/groups`, { headers });
        const nextGroups = response.data.groups || [];
        setGroups(nextGroups);
        if (nextGroups.length > 0) {
          setSelectedGroup((current) => current || nextGroups[0].id);
          setSelectedGroupName((current) => current || nextGroups[0].name);
        }
      } catch (error) {
        console.error("Failed to fetch spillover groups", error);
      } finally {
        setLoadingGroups(false);
      }
    };

    if (token) {
      fetchGroups();
    }
  }, [headers, token]);

  useEffect(() => {
    const matched = groups.find((group) => group.id === selectedGroup);
    setSelectedGroupName(matched?.name || "");
  }, [groups, selectedGroup]);

  const fetchAnalysis = useCallback(async () => {
    if (!selectedGroup) return;
    setLoading(true);
    setAnalysis(null);
    setExpandedEvent(null);
    try {
      const response = await axios.get(`${API}/spillover/analysis`, {
        headers,
        params: { group_id: selectedGroup, days },
      });
      setAnalysis(response.data);
    } catch (error) {
      toast.error(t.analysisFailed);
    } finally {
      setLoading(false);
    }
  }, [days, headers, selectedGroup, t.analysisFailed]);

  const fetchTimeline = useCallback(async () => {
    if (!selectedGroup) return;
    setLoadingTimeline(true);
    setTimeline(null);
    try {
      const response = await axios.get(`${API}/spillover/timeline`, {
        headers,
        params: { group_id: selectedGroup, days },
      });
      setTimeline(response.data);
    } catch (error) {
      toast.error(t.timelineFailed);
    } finally {
      setLoadingTimeline(false);
    }
  }, [days, headers, selectedGroup, t.timelineFailed]);

  const fetchComparison = useCallback(async () => {
    if (!selectedGroup || !compareMode) return;
    setLoadingComparison(true);
    setPreviousAnalysis(null);
    setPreviousTimeline(null);
    try {
      const now = new Date();
      now.setHours(now.getHours() + 8);
      const currentEnd = now.toISOString().slice(0, 10);
      const currentStart = new Date(now.getTime() - days * 86400000).toISOString().slice(0, 10);
      const prevEnd = currentStart;
      const prevStart = new Date(new Date(currentStart).getTime() - days * 86400000).toISOString().slice(0, 10);

      const [analysisRes, timelineRes] = await Promise.all([
        axios.get(`${API}/spillover/analysis`, {
          headers,
          params: { group_id: selectedGroup, days, start_date: prevStart, end_date: prevEnd },
        }),
        axios.get(`${API}/spillover/timeline`, {
          headers,
          params: { group_id: selectedGroup, days, start_date: prevStart, end_date: prevEnd },
        }),
      ]);
      setPreviousAnalysis(analysisRes.data);
      setPreviousTimeline(timelineRes.data);
    } catch {
      toast.error(t.comparisonFailed);
    } finally {
      setLoadingComparison(false);
    }
  }, [compareMode, days, headers, selectedGroup, t.comparisonFailed]);

  useEffect(() => {
    if (selectedGroup) {
      fetchAnalysis();
      fetchTimeline();
    }
  }, [selectedGroup, days, fetchAnalysis, fetchTimeline]);

  useEffect(() => {
    if (compareMode && selectedGroup) {
      fetchComparison();
    } else {
      setPreviousAnalysis(null);
      setPreviousTimeline(null);
    }
  }, [compareMode, selectedGroup, days, fetchComparison]);

  const availablePlatforms = analysis?.summary?.platforms_analyzed || [];

  const filteredEvents = useMemo(() => {
    const events = analysis?.events || [];
    return events.filter((event) => {
      const sourceMatches = sourceFilter === "all" || event.source_account.platform === sourceFilter;
      const targetMatches = targetFilter === "all" || event.effects.some((effect) => effect.platform === targetFilter);
      return sourceMatches && targetMatches;
    });
  }, [analysis?.events, sourceFilter, targetFilter]);

  const chartData = useMemo(() => {
    if (!timeline?.platform_timelines?.length) return [];
    const dateMap = {};
    for (const timelineItem of timeline.platform_timelines) {
      for (const point of timelineItem.data) {
        if (!dateMap[point.date]) {
          dateMap[point.date] = { date: point.date };
        }
        dateMap[point.date][timelineItem.platform] = point.followers;
      }
    }
    return Object.values(dateMap).sort((left, right) => left.date.localeCompare(right.date));
  }, [timeline?.platform_timelines]);

  const activePlatforms = useMemo(() => timeline?.platform_timelines?.map((timelineItem) => timelineItem.platform) || [], [timeline?.platform_timelines]);
  const viralMarkers = analysis?.events?.map((event) => ({
    date: event.viral_post.published_at?.slice(0, 10),
    platform: event.viral_post.platform,
    title: event.viral_post.title,
  })) || [];

  const overlayChartData = useMemo(() => {
    if (!compareMode || !timeline?.platform_timelines?.length) return null;
    const currDates = chartData.map((d) => d.date).sort();
    const prevTimelines = previousTimeline?.platform_timelines || [];
    if (!prevTimelines.length || !currDates.length) return null;

    const prevDateMap = {};
    for (const tl of prevTimelines) {
      for (const pt of tl.data) {
        if (!prevDateMap[pt.date]) prevDateMap[pt.date] = {};
        prevDateMap[pt.date][tl.platform] = pt.followers;
      }
    }
    const prevDates = Object.keys(prevDateMap).sort();

    const merged = [];
    const maxLen = Math.max(currDates.length, prevDates.length);
    for (let i = 0; i < maxLen; i++) {
      const dayLabel = `Day ${i + 1}`;
      const row = { day: dayLabel };
      const currEntry = chartData[i];
      const prevDate = prevDates[i];
      for (const platform of activePlatforms) {
        if (currEntry) row[`curr_${platform}`] = currEntry[platform] ?? null;
        if (prevDate && prevDateMap[prevDate]) row[`prev_${platform}`] = prevDateMap[prevDate][platform] ?? null;
      }
      merged.push(row);
    }
    return merged;
  }, [compareMode, chartData, previousTimeline, activePlatforms, timeline]);

  if (loadingGroups) {
    return (
      <div className="space-y-4" data-testid="spillover-loading-state">
        <Skeleton className="h-12 w-80" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <Card data-testid="spillover-no-groups">
        <CardContent className="py-12 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground/40" />
          <p className="font-medium text-muted-foreground">{t.noGroups}</p>
          <p className="mt-1 text-sm text-muted-foreground/70">{t.noGroupsDesc}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="spillover-page">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-3xl space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.25em] text-yellow-600">
            <Zap className="h-3.5 w-3.5" />
            Spillover
          </div>
          <h1 className="text-4xl font-heading font-bold tracking-tight sm:text-5xl">{t.title}</h1>
          <p className="text-sm text-muted-foreground sm:text-base">{t.subtitle}</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <Select value={selectedGroup} onValueChange={setSelectedGroup}>
            <SelectTrigger data-testid="group-selector" className="w-full sm:w-[220px]">
              <SelectValue placeholder={t.selectGroup} />
            </SelectTrigger>
            <SelectContent>
              {groups.map((group) => (
                <SelectItem key={group.id} value={group.id}>{group.name} ({group.account_count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(days)} onValueChange={(value) => setDays(Number(value))}>
            <SelectTrigger data-testid="period-selector" className="w-full sm:w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[7, 14, 30, 60, 90].map((value) => (
                <SelectItem key={value} value={String(value)}>{t[`days${value}`]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-background/70 px-3 py-1.5" data-testid="compare-mode-toggle">
            <GitCompare className="h-3.5 w-3.5 text-muted-foreground" />
            <label htmlFor="compare-switch" className="text-xs font-medium text-muted-foreground whitespace-nowrap cursor-pointer">{t.compareMode}</label>
            <Switch id="compare-switch" checked={compareMode} onCheckedChange={setCompareMode} data-testid="compare-mode-switch" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-28 w-full" />)}
        </div>
      ) : analysis?.summary ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryMetricCard
            title={t.viralPosts}
            value={analysis.summary.total_viral_posts}
            subvalue={`${analysis.summary.total_spillover_events} events`}
            icon={Zap}
            hint={t.viralScoreHelp}
            testId="summary-viral-posts"
          />
          <SummaryMetricCard
            title={t.positiveRatio}
            value={`${analysis.summary.positive_effect_ratio || 0}%`}
            subvalue={t.positiveEvents}
            icon={TrendingUp}
            hint={t.positiveRatioHelp}
            testId="summary-positive-ratio"
          />
          <SummaryMetricCard
            title={t.strongestSource}
            value={analysis.summary.strongest_source_platform ? getLocalizedPlatformName(analysis.summary.strongest_source_platform.platform, lang) : "-"}
            subvalue={analysis.summary.strongest_source_platform ? getSignedCompactNumber(analysis.summary.strongest_source_platform.total_follower_delta) : undefined}
            icon={Network}
            toneClass={analysis.summary.strongest_source_platform ? getDeltaTone(analysis.summary.strongest_source_platform.total_follower_delta) : undefined}
            testId="summary-strongest-source"
          />
          <SummaryMetricCard
            title={t.strongestTarget}
            value={analysis.summary.strongest_target_platform ? getLocalizedPlatformName(analysis.summary.strongest_target_platform.platform, lang) : "-"}
            subvalue={analysis.summary.strongest_target_platform ? getSignedCompactNumber(analysis.summary.strongest_target_platform.total_follower_delta) : undefined}
            icon={Users}
            toneClass={analysis.summary.strongest_target_platform ? getDeltaTone(analysis.summary.strongest_target_platform.total_follower_delta) : undefined}
            testId="summary-strongest-target"
          />
        </div>
      ) : null}

      {!loading && analysis?.summary?.top_platform_pair ? (
        <TopPairCard pair={analysis.summary.top_adjusted_platform_pair || analysis.summary.top_platform_pair} lang={lang} t={t} />
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="spillover-tabs-root">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-2xl bg-muted/40 p-1">
          <TabsTrigger value="flow-network" data-testid="flow-network-tab">{t.flowNetworkTab}</TabsTrigger>
          <TabsTrigger value="events-timeline" data-testid="events-timeline-tab">{t.eventsTimelineTab}</TabsTrigger>
          <TabsTrigger value="strategy-export" data-testid="strategy-export-tab">{t.strategyExportTab}</TabsTrigger>
        </TabsList>

        {/* Tab 1: Flow & Network */}
        <TabsContent value="flow-network" className="space-y-4">
          {compareMode ? (
            <SpilloverComparison
              currentAnalysis={analysis}
              previousAnalysis={previousAnalysis}
              lang={lang}
              loading={loadingComparison}
            />
          ) : null}

          {!analysis?.events?.length && !compareMode ? (
            <Card data-testid="spillover-empty-flow">
              <CardContent className="py-12 text-center">
                <Network className="mx-auto mb-3 h-10 w-10 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t.noEvents}</p>
                <p className="mt-1 text-xs text-muted-foreground/60">{t.noEventsDesc}</p>
              </CardContent>
            </Card>
          ) : analysis?.events?.length ? (
            <>
              <Card data-testid="spillover-viz-controls-card" className="border-border/60">
                <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                  <ToggleGroup
                    type="single"
                    value={vizMode}
                    onValueChange={(value) => value && setVizMode(value)}
                    className="justify-start"
                    data-testid="spillover-viz-toggle"
                  >
                    <ToggleGroupItem value="matrix" className="gap-1.5 rounded-xl px-3 text-xs" data-testid="viz-toggle-matrix">
                      <Grid3x3 className="h-3.5 w-3.5" /> {t.matrixView}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="graph" className="gap-1.5 rounded-xl px-3 text-xs" data-testid="viz-toggle-graph">
                      <GitBranch className="h-3.5 w-3.5" /> {t.graphView}
                    </ToggleGroupItem>
                    <ToggleGroupItem value="sankey" className="gap-1.5 rounded-xl px-3 text-xs" data-testid="viz-toggle-sankey">
                      <TrendingUp className="h-3.5 w-3.5" /> {t.sankeyView}
                    </ToggleGroupItem>
                  </ToggleGroup>

                  {vizMode !== "matrix" ? (
                    <div className="grid gap-2 sm:grid-cols-2" data-testid="spillover-network-controls">
                      <Select value={metricMode} onValueChange={setMetricMode}>
                        <SelectTrigger data-testid="spillover-metric-select" className="w-full sm:w-[170px]">
                          <SelectValue placeholder={t.metricLabel} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adjusted">{t.adjustedMetric}</SelectItem>
                          <SelectItem value="observed">{t.observedMetric}</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
                        <SelectTrigger data-testid="spillover-confidence-select" className="w-full sm:w-[140px]">
                          <SelectValue placeholder={t.confidenceLabel} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t.allFilter}</SelectItem>
                          <SelectItem value="high">{t.high}</SelectItem>
                          <SelectItem value="medium">{t.medium}</SelectItem>
                          <SelectItem value="low">{t.low}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {vizMode === "matrix" && <PairMatrix analysis={analysis} lang={lang} t={t} />}

              {(vizMode === "graph" || vizMode === "sankey") && (
                <SpilloverNetworkVisualization
                  analysis={analysis}
                  lang={lang}
                  loading={loading}
                  snapshotRef={networkSnapshotRef}
                  viewMode={vizMode}
                  metricMode={metricMode}
                  confidenceFilter={confidenceFilter}
                />
              )}

              <Accordion type="multiple" defaultValue={["rankings"]} className="space-y-2" data-testid="spillover-flow-accordion">
                <AccordionItem value="rankings" className="rounded-2xl border border-border/60">
                  <AccordionTrigger className="px-4 text-sm font-semibold" data-testid="spillover-rankings-trigger">{t.rankingsTitle}</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="grid gap-4 xl:grid-cols-2">
                      <RankingCard title={t.sourceRanking} items={analysis.source_platform_summary} lang={lang} t={t} testId="source-ranking-card" type="source" />
                      <RankingCard title={t.targetRanking} items={analysis.target_platform_summary} lang={lang} t={t} testId="target-ranking-card" type="target" />
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="methodology" className="rounded-2xl border border-border/60">
                  <AccordionTrigger className="px-4 text-sm font-semibold" data-testid="spillover-methodology-trigger">{t.methodology}</AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p><strong>{t.observationWindow}:</strong> {analysis?.methodology?.snapshot_rule}</p>
                      <p><strong>Viral threshold:</strong> {analysis?.methodology?.viral_threshold}</p>
                      <p><strong>{t.estimateLabel}:</strong> {analysis?.methodology?.interpretation_note}</p>
                      <p><strong>Baseline:</strong> {analysis?.methodology?.minimum_posts_for_baseline} posts minimum for viral detection.</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </>
          ) : null}
        </TabsContent>

        {/* Tab 2: Events & Timeline */}
        <TabsContent value="events-timeline" className="space-y-4">
          {loadingTimeline ? (
            <Skeleton className="h-96 w-full" />
          ) : !timeline?.data_available ? (
            <Card data-testid="spillover-timeline-empty-state">
              <CardContent className="py-8 text-center">
                <TrendingUp className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t.noData}</p>
              </CardContent>
            </Card>
          ) : compareMode && overlayChartData ? (
            <Card data-testid="spillover-timeline-overlay-card" className="border-primary/20 bg-gradient-to-b from-primary/4 via-background to-background">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <GitCompare className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-semibold">{t.timelineTab} — {t.compareMode}</CardTitle>
                </div>
                <CardDescription>
                  {t.currentPeriod}: {lang === "ko" ? "실선" : lang === "zh" ? "实线" : "Solid"} / {t.previousPeriod}: {lang === "ko" ? "점선" : lang === "zh" ? "虚线" : "Dashed"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[320px] md:h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={overlayChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis dataKey="day" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(value) => formatNumber(value)} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 14,
                          fontSize: 12,
                        }}
                        formatter={(value, name) => {
                          const isPrev = name.startsWith("prev_");
                          const platform = name.replace(/^(curr_|prev_)/, "");
                          const label = `${getLocalizedPlatformName(platform, lang)} (${isPrev ? t.previousPeriod : t.currentPeriod})`;
                          return [formatNumber(value), label];
                        }}
                      />
                      <Legend
                        formatter={(value) => {
                          const isPrev = value.startsWith("prev_");
                          const platform = value.replace(/^(curr_|prev_)/, "");
                          return `${getLocalizedPlatformName(platform, lang)} ${isPrev ? `(${t.previousPeriod})` : `(${t.currentPeriod})`}`;
                        }}
                      />
                      {activePlatforms.map((platform) => (
                        <Fragment key={`overlay-${platform}`}>
                          <Line
                            type="monotone"
                            dataKey={`curr_${platform}`}
                            name={`curr_${platform}`}
                            stroke={PLATFORM_CHART_COLORS[platform] || "#888"}
                            strokeWidth={2.5}
                            dot={false}
                            connectNulls
                          />
                          <Line
                            type="monotone"
                            dataKey={`prev_${platform}`}
                            name={`prev_${platform}`}
                            stroke={PLATFORM_CHART_COLORS[platform] || "#888"}
                            strokeWidth={1.5}
                            strokeDasharray="6 3"
                            strokeOpacity={0.5}
                            dot={false}
                            connectNulls
                          />
                        </Fragment>
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card data-testid="spillover-timeline-card" className="border-border/60">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold">{t.timelineTab}</CardTitle>
                <CardDescription>{timeline.group?.name} · {timeline.date_range?.start} ~ {timeline.date_range?.end}</CardDescription>
                <p className="text-xs text-muted-foreground">{t.timelineHint}</p>
              </CardHeader>
              <CardContent>
                <div className="h-[320px] md:h-[380px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(date) => date.slice(5)} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(value) => formatNumber(value)} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--popover))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: 14,
                          fontSize: 12,
                        }}
                        formatter={(value, name) => [formatNumber(value), getLocalizedPlatformName(name, lang)]}
                      />
                      <Legend formatter={(value) => getLocalizedPlatformName(value, lang)} />
                      {viralMarkers.slice(0, 12).map((marker, index) => (
                        <ReferenceLine
                          key={`${marker.platform}-${marker.date}-${index}`}
                          x={marker.date}
                          stroke={PLATFORM_CHART_COLORS[marker.platform] || "#666"}
                          strokeDasharray="4 4"
                          strokeOpacity={0.45}
                        />
                      ))}
                      {activePlatforms.map((platform) => (
                        <Line
                          key={platform}
                          type="monotone"
                          dataKey={platform}
                          name={platform}
                          stroke={PLATFORM_CHART_COLORS[platform] || "#888"}
                          strokeWidth={2}
                          dot={false}
                          connectNulls
                        />
                      ))}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                {viralMarkers.length > 0 ? (
                  <div className="mt-3 border-t border-border/40 pt-3">
                    <p className="mb-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground/60">{t.posts}</p>
                    <div className="flex flex-wrap gap-2">
                      {viralMarkers.slice(0, 8).map((marker, index) => (
                        <Badge key={`${marker.platform}-${marker.date}-${index}`} variant="outline" className="gap-2 rounded-full px-3 py-1 text-[10px]" data-testid={`timeline-marker-badge-${index}`}>
                          <span style={{ color: PLATFORM_CHART_COLORS[marker.platform] }}>{getLocalizedPlatformName(marker.platform, lang)}</span>
                          <span className="text-muted-foreground">{marker.date?.slice(5)} · {marker.title?.slice(0, 20)}</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}

          <Card data-testid="spillover-events-filters-card" className="border-border/60">
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Filter className="h-4 w-4 text-primary" />
                {t.eventsTab}
              </div>
              <div className="grid gap-2 sm:grid-cols-2 md:w-[420px]">
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger data-testid="spillover-source-filter">
                    <SelectValue placeholder={t.allSources} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.allSources}</SelectItem>
                    {availablePlatforms.map((platform) => (
                      <SelectItem key={`source-${platform}`} value={platform}>{getLocalizedPlatformName(platform, lang)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={targetFilter} onValueChange={setTargetFilter}>
                  <SelectTrigger data-testid="spillover-target-filter">
                    <SelectValue placeholder={t.allTargets} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t.allTargets}</SelectItem>
                    {availablePlatforms.map((platform) => (
                      <SelectItem key={`target-${platform}`} value={platform}>{getLocalizedPlatformName(platform, lang)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} className="h-40 w-full" />)}
            </div>
          ) : !filteredEvents.length ? (
            <Card data-testid="spillover-events-empty-state">
              <CardContent className="py-8 text-center">
                <Zap className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">{t.noEvents}</p>
                <p className="mt-1 text-xs text-muted-foreground/60">{t.noEventsDesc}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredEvents.map((event, index) => (
                <EventCard
                  key={`${event.viral_post.id}-${index}`}
                  event={event}
                  index={index}
                  expanded={expandedEvent === index}
                  onToggle={setExpandedEvent}
                  targetFilter={targetFilter}
                  lang={lang}
                  t={t}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab 3: Strategy & Export */}
        <TabsContent value="strategy-export" className="space-y-6">
          <PlaybookTab analysis={analysis} lang={lang} t={t} />

          <SpilloverPatternInsights lang={lang} headers={headers} selectedGroup={selectedGroup} days={days} />

          <ContentStrategyReport lang={lang} headers={headers} selectedGroup={selectedGroup} days={days} />

          <SpilloverExportCenter
            lang={lang}
            headers={headers}
            selectedGroup={selectedGroup}
            selectedGroupName={selectedGroupName}
            days={days}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            networkSnapshotRef={networkSnapshotRef}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
